import type { AnalysisEvidence } from "@/types";

/**
 * AnalysisGapsNote — 결과에 포함되지 않은 것 + 적용 기준 (검출 표시 개선 1단계).
 *
 * 백엔드는 완료 노트(analysis.error_message)에 "배경가스라 제외한 스캔 N건",
 * "후보 전원이 기준 미달이라 기록하지 않은 스캔 N건" 같은 사실을 남기는데,
 * 지금까지 분석 상세 페이지에만 보였고 **대시보드는 그리지 않았다** — 화면만
 * 보면 무엇이 빠졌는지 알 수 없었다. 여기서 함께 보여준다.
 *
 * 적용 기준(top_k·매칭 점수 하한)은 불변 증거 스냅숏(evidence.parameters)에서
 * 읽는다 — 화면이 재구성한 값이 아니라 분석 시점에 기록된 값이다. 기준값은
 * 우리 내부 참고값이며 판정어를 붙이지 않는다(과제 기준 확정 전).
 */
interface AnalysisGapsNoteProps {
  status?: string;
  errorMessage?: string | null;
  evidence?: AnalysisEvidence | null;
}

export default function AnalysisGapsNote({
  status,
  errorMessage,
  evidence,
}: AnalysisGapsNoteProps) {
  // 완료 분석 전용 — 실패 분석의 error_message 는 실패 사유라 성격이 다르다.
  if (status !== "completed") return null;

  const params = evidence?.parameters ?? null;
  const belowCount = params?.below_threshold_candidates ?? null;
  const hasGaps = !!errorMessage;
  const hasParams = params?.top_k != null && params?.min_match_score != null;
  if (!hasGaps && !hasParams) return null;

  return (
    <div
      data-testid="analysis-gaps-note"
      className="mb-4 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-2.5 text-xs text-amber-900"
    >
      {hasGaps && (
        <p>
          <span className="font-semibold">결과에 포함되지 않은 것</span> —{" "}
          {errorMessage}
        </p>
      )}
      {hasParams && (
        <p className={hasGaps ? "mt-1 text-amber-800/90" : "text-amber-800/90"}>
          <span className="font-semibold">적용 기준</span> — 스캔당 후보 상한{" "}
          {params?.top_k} · 매칭 점수 하한 {params?.min_match_score}
          {belowCount != null && belowCount > 0 && (
            <> · 기준 미달로 기록하지 않은 후보 {belowCount}건</>
          )}{" "}
          <span className="text-amber-700/80">
            (내부 참고 기준 — 과제 기준 확정 전, 판정값 아님)
          </span>
        </p>
      )}
    </div>
  );
}
